import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import chroma from "chroma-js";
import Icon from "components/common/Icon";
import zIndices from "theme/z-indices";
const HINT_VISIBILITY_DURATION_MS = 2e3;
const HINT_WIDTH_PX = 200;
const ScrollHint = styled.button(
  ({ theme }) => css`
    position: fixed;
    left: calc(50% - ${HINT_WIDTH_PX / 2}px);
    top: 50px;
    color: ${theme.utils.readableColor(chroma(theme.colors.brand.tertiary).alpha(0.8).css())};
    font-size: ${theme.fonts.size.huge};
    padding: 20px;
    z-index: ${zIndices.indicator};
    width: ${HINT_WIDTH_PX}px;
    border-radius: 10px;
    border: 0;
    background: ${chroma(theme.colors.brand.tertiary).alpha(0.8).css()};
  `
);
export const isElementVisibleInContainer = (target, scrollContainer) => {
  const containerRect = scrollContainer.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return targetRect.top >= containerRect.top && targetRect.bottom <= containerRect.bottom;
};
const ScrollToHint = ({
  ifValueChanges = void 0,
  scrollContainer,
  title,
  ifTrue = true,
  autoScroll = false,
  onSettled = void 0
}) => {
  const scrollTargetRef = useRef(null);
  const [showHint, setShowHint] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef(null);
  const scrollToTarget = useCallback(() => {
    setShowHint(false);
    setIsHovered(false);
    if (scrollTargetRef.current) {
      scrollTargetRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);
  useEffect(() => {
    var _a;
    if (!ifTrue || !scrollTargetRef.current || !scrollContainer.current) {
      return;
    }
    if (isElementVisibleInContainer(
      (_a = scrollTargetRef.current.parentElement) != null ? _a : scrollTargetRef.current,
      scrollContainer.current
    )) {
      onSettled == null ? void 0 : onSettled();
      return;
    }
    if (autoScroll) {
      scrollToTarget();
      onSettled == null ? void 0 : onSettled();
    } else {
      setShowHint(true);
    }
  }, [ifTrue, ifValueChanges, setShowHint, scrollContainer, scrollToTarget, autoScroll, onSettled]);
  useEffect(() => {
    if (showHint && !isHovered) {
      timeoutRef.current = setTimeout(() => setShowHint(false), HINT_VISIBILITY_DURATION_MS);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showHint, isHovered]);
  const iconName = useMemo(() => {
    var _a, _b;
    const currentScroll = (_a = scrollContainer.current) == null ? void 0 : _a.scrollTop;
    const elementTop = (_b = scrollTargetRef == null ? void 0 : scrollTargetRef.current) == null ? void 0 : _b.getBoundingClientRect().top;
    return elementTop !== void 0 && currentScroll !== void 0 && elementTop > currentScroll ? "arrow_downward" : "arrow_upward";
  }, [scrollContainer]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { ref: scrollTargetRef }), showHint && /* @__PURE__ */ React.createElement(
    ScrollHint,
    {
      onClick: scrollToTarget,
      "aria-label": title,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false)
    },
    /* @__PURE__ */ React.createElement(Icon, { name: iconName })
  ));
};
export default ScrollToHint;
